import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { trackFunnelEvent } from '@/utils/tracking';
import { ADS_CONVERSION_GUARD_PREFIX } from './MonitoringSuccessPage';

const API = '/api';

// Legacy report-purchase Ads conversion label. Hard-coded historisch
// (eerste publicatie pre-EC-promo). Stripe session_id wordt als
// transaction_id meegestuurd voor Ads-side dedupe.
export const LEGACY_PAYMENT_ADS_SEND_TO = 'AW-18091985325/9w2vCMiotpwcEK2T97JD';

/**
 * Vuurt eenmalig per Stripe session_id de legacy purchase tracking
 * (Google Ads conversion + interne pwTrack). Idempotent via dezelfde
 * sessionStorage-prefix als MonitoringSuccessPage zodat één session
 * over beide success-pagina's heen maximaal één Ads-conversion vuurt.
 *
 * Returns: { fired: boolean, reason: string }
 *   - fired=false reasons: no_session_id | already_fired | gtag_not_loaded | storage_set_failed
 */
export function _fireLegacyPaymentTracking(sessionId, data, deps = {}) {
  const _gtag = deps.gtag !== undefined ? deps.gtag : (typeof window !== 'undefined' ? window.gtag : undefined);
  const _pwTrack = deps.pwTrack !== undefined ? deps.pwTrack : (typeof window !== 'undefined' ? window.pwTrack : undefined);
  const _storage = deps.sessionStorage !== undefined ? deps.sessionStorage : (typeof window !== 'undefined' ? window.sessionStorage : undefined);

  if (!sessionId) return { fired: false, reason: 'no_session_id' };

  // Cross-page idempotency guard: zelfde prefix als MonitoringSuccessPage.
  // Eén keer gevuurd voor deze session_id → geen tweede Ads-conversion.
  const guardKey = `${ADS_CONVERSION_GUARD_PREFIX}${sessionId}`;
  try {
    if (_storage && _storage.getItem(guardKey)) {
      return { fired: false, reason: 'already_fired' };
    }
  } catch {
    // storage unavailable — proceed but skip dedupe write
  }

  // Mark guard FIRST, then fire. Voorkomt race waarbij twee snel-na-elkaar
  // verifyPayment-retries beide het gtag-blok binnenkomen.
  try { _storage && _storage.setItem(guardKey, '1'); } catch { /* ignore */ }

  if (typeof _pwTrack === 'function') {
    try {
      _pwTrack('payment_success', {
        report_id: data?.report_id,
        value: data?.amount || 59,
        currency: 'EUR',
        transaction_id: data?.transaction_id || sessionId,
      });
    } catch { /* swallow tracking errors */ }
  }

  if (typeof _gtag !== 'function') {
    return { fired: false, reason: 'gtag_not_loaded' };
  }

  try {
    _gtag('event', 'conversion', {
      send_to: LEGACY_PAYMENT_ADS_SEND_TO,
      value: data?.amount || 0,
      currency: 'EUR',
      transaction_id: data?.transaction_id || sessionId,
    });
    return { fired: true, reason: 'ok' };
  } catch (err) {
    return { fired: false, reason: 'gtag_threw', error: String(err).slice(0, 200) };
  }
}

export default function PaymentSuccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  const verifyPayment = useCallback(async (attempts = 0) => {
    if (!sessionId) {
      setStatus('error');
      setError('No session ID found');
      return;
    }
    if (attempts >= 8) {
      setStatus('error');
      setError(t('payment.verifyTimeout'));
      return;
    }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      if (data.payment_status === 'paid') {
        setStatus('success');
        // Funnel: emit payment_completed ONCE per Stripe session_id. Avoids
        // duplicate events on page refresh / verifyPayment retry / browser
        // back-forward cache.
        const guardKey = `ps_payment_completed_${sessionId}`;
        if (!sessionStorage.getItem(guardKey)) {
          trackFunnelEvent('payment_completed', {
            report_id: data.report_id,
            stripe_session_id: sessionId,
          });
          sessionStorage.setItem(guardKey, '1');
        }
        // Ads + pwTrack: één keer per session_id, cross-page-safe.
        _fireLegacyPaymentTracking(sessionId, data);
        setTimeout(() => navigate(`/report/${data.report_id}`), 2500);
      } else if (data.status === 'expired') {
        setStatus('error');
        setError(t('payment.expired'));
      } else {
        setTimeout(() => verifyPayment(attempts + 1), 2000);
      }
    } catch {
      setStatus('error');
      setError(t('payment.verifyError'));
    }
  }, [sessionId, navigate, t]);

  useEffect(() => {
    verifyPayment();
  }, [verifyPayment]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center p-8">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>
              {t('payment.verifying')}
            </h1>
            <p className="text-muted-foreground">{t('payment.verifyingDesc')}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>
              {t('payment.successTitle')}
            </h1>
            <p className="text-muted-foreground">{t('payment.successDesc')}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <span className="text-destructive text-xl font-bold">!</span>
            </div>
            <h1 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>
              {t('payment.verifyFailedTitle')}
            </h1>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="btn-primary px-6 py-2 rounded-md"
              data-testid="payment-error-home-btn"
            >
              {t('common.backToHome')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
